import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider } from '@dnd-kit/react';

import Button from 'components/shared/Button';
import Menu from 'components/shared/menu';
import { colors } from 'styles/theme/colors';
import DeactivateIcon from 'svg/DeactivateIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';

import { type CriteriaItem, generateTempCriteriaId } from './CategoriesList';
import CategoryDeleteModal from './CategoryDeleteModal';
import CriteriaDeleteConfirm from './CriteriaDeleteConfirm';
import CriteriaForm from './CriteriaForm';
import CriteriaListView from './CriteriaListView';
import NewCategoryModal, { CATEGORY_COLORS, type CriteriaCategory } from './NewCategoryModal';

const COLOR_DOT_MAP: Record<string, string> = {
  green: colors.green100,
  yellow: colors.yellow100,
  red: colors.red100,
  orange: colors.orange100,
  blue: colors.blue100,
};

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

type SlideoutView =
  | { type: 'list' }
  | { type: 'add' }
  | { type: 'edit'; key: string }
  | { type: 'delete'; key: string };

type Props = {
  handleClose: () => void;
  category: CriteriaCategory;
  criteria: CriteriaItem[];
  allCategories: CriteriaCategory[];
  onEditCategory: (updates: Pick<CriteriaCategory, 'name' | 'color'>) => void;
  onDeleteCategory: () => void;
  onApply: (updatedCriteria: CriteriaItem[]) => void;
  inheritedCriteria?: CriteriaItem[];
  hiddenAiCriteriaIds?: string[];
  /** Called on Apply with the full updated set of hidden inherited-criteria ids (buffered until Apply). */
  onApplyHidden?: (ids: string[]) => void;
  /**
   * True when the category itself is owned by the client (inherited). The category's name/color stay
   * read-only (it remains the client's, so a client rename flows through), but the group can still add
   * and edit its OWN criteria under it.
   */
  isInherited?: boolean;
  /** True when editing a client-level set, so deleting a category cascades to every group that inherits it. */
  cascadesToGroups?: boolean;
};

export default function CriteriaSlideout({
  handleClose,
  category,
  criteria,
  allCategories,
  onEditCategory,
  onDeleteCategory,
  onApply,
  inheritedCriteria = [],
  hiddenAiCriteriaIds = [],
  onApplyHidden,
  isInherited = false,
  cascadesToGroups = false,
}: Props) {
  const [view, setView] = useState<SlideoutView>({ type: 'list' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form state for add/edit
  const [formCriterion, setFormCriterion] = useState('');
  const [formDescription, setFormDescription] = useState('');

  // Local buffer: changes are staged here and only applied on "Apply"
  const [localCriteria, setLocalCriteria] = useState<CriteriaItem[]>(() => [...criteria]);
  const [localCategory, setLocalCategory] = useState<Pick<CriteriaCategory, 'name' | 'color'>>({
    name: category.name,
    color: category.color,
  });
  // Hide/unhide toggles are buffered here and only committed on Apply, like every other edit.
  const [localHiddenIds, setLocalHiddenIds] = useState<string[]>(() => [...hiddenAiCriteriaIds]);
  const [hasChanges, setHasChanges] = useState(false);

  const getKey = (c: CriteriaItem) => c.id || c._tempId || '';

  const filteredCriteria = useMemo(() => {
    const mapped = localCriteria.map((c) => ({ ...c, _key: getKey(c) }));

    return searchTerm
      ? mapped.filter(
          (c) =>
            c.criterion.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : mapped;
  }, [localCriteria, searchTerm]);

  const filteredInheritedCriteria = useMemo(() => {
    const mapped = inheritedCriteria.map((c) => {
      const key = getKey(c);

      return { ...c, _key: key, hidden: localHiddenIds.includes(key) };
    });

    return searchTerm
      ? mapped.filter(
          (c) =>
            c.criterion.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : mapped;
  }, [inheritedCriteria, localHiddenIds, searchTerm]);

  const findIndexByKey = (key: string) => localCriteria.findIndex((c) => getKey(c) === key);

  const toggleLocalHidden = (criteriaId: string) => {
    setLocalHiddenIds((prev) =>
      prev.includes(criteriaId) ? prev.filter((id) => id !== criteriaId) : [...prev, criteriaId]
    );
    setHasChanges(true);
  };

  const handleApply = () => {
    // Flush buffered hide/unhide toggles if they actually changed.
    const hiddenChanged =
      localHiddenIds.length !== hiddenAiCriteriaIds.length ||
      localHiddenIds.some((id) => !hiddenAiCriteriaIds.includes(id));

    if (hiddenChanged) onApplyHidden?.(localHiddenIds);

    // The category name/color are only editable for group-owned categories. For an inherited
    // category the group can still add/edit its own criteria, but the category stays the client's.
    if (!isInherited && (localCategory.name !== category.name || localCategory.color !== category.color)) {
      onEditCategory(localCategory);
    }

    onApply(localCriteria);
    setHasChanges(false);
    handleClose();
  };

  const handleAdd = () => {
    setFormCriterion('');
    setFormDescription('');
    setView({ type: 'add' });
  };

  const handleEditClick = (key: string) => {
    const index = findIndexByKey(key);

    if (index === -1) return;

    const item = localCriteria[index];

    setFormCriterion(item.criterion);
    setFormDescription(item.description);
    setView({ type: 'edit', key });
  };

  const handleDeleteClick = (key: string) => {
    setView({ type: 'delete', key });
  };

  const handleSaveCriteria = () => {
    if (!formCriterion.trim()) return;

    if (view.type === 'edit') {
      const index = findIndexByKey(view.key);

      if (index === -1) return;

      const updated = [...localCriteria];

      updated[index] = {
        ...updated[index],
        criterion: formCriterion,
        description: formDescription,
      };
      setLocalCriteria(updated);
    } else {
      setLocalCriteria([
        ...localCriteria,
        {
          _tempId: generateTempCriteriaId(),
          criterion: formCriterion,
          description: formDescription,
          // `category` (name) stays as the display/fallback value; `categoryId` is the persisted
          // linkage used once the category has an id. For an inherited category, `category.id` here
          // is the client category's id -- no group-owned mirror category is created.
          category: localCategory.name,
          ...(category.id ? { categoryId: category.id } : {}),
          order: localCriteria.length,
        },
      ]);
    }

    setHasChanges(true);
    setView({ type: 'list' });
  };

  const handleConfirmDelete = () => {
    if (view.type !== 'delete') return;

    const index = findIndexByKey(view.key);

    if (index === -1) return;

    setLocalCriteria(localCriteria.filter((_, i) => i !== index));
    setHasChanges(true);
    setView({ type: 'list' });
  };

  const handleDragEnd = useCallback(
    (...[event]: Parameters<DragEndHandler>) => {
      if (event.canceled) return;
      const { source } = event.operation;

      if (!source || !isSortable(source)) return;

      const oldIndex = source.initialIndex;
      const newIndex = source.index;

      if (oldIndex === newIndex) return;

      const reordered = [...localCriteria];
      const [moved] = reordered.splice(oldIndex, 1);

      reordered.splice(newIndex, 0, moved);
      setLocalCriteria(reordered);
      setHasChanges(true);
    },
    [localCriteria]
  );

  const isSearching = searchTerm.length > 0;

  const getFormSaveDisabled = () => {
    if (!formCriterion.trim()) return true;

    if (view.type === 'edit') {
      const index = findIndexByKey(view.key);
      const editItem = index !== -1 ? localCriteria[index] : null;

      return editItem ? formCriterion === editItem.criterion && formDescription === editItem.description : false;
    }

    return false;
  };

  const getContent = () => {
    switch (view.type) {
      case 'list':
        return (
          <CriteriaListView
            criteria={filteredCriteria}
            inheritedCriteria={filteredInheritedCriteria}
            onToggleHidden={toggleLocalHidden}
            isSearching={isSearching}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAdd={handleAdd}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onDragEnd={handleDragEnd}
          />
        );
      case 'add':
      case 'edit':
        return (
          <CriteriaForm
            isEdit={view.type === 'edit'}
            criterion={formCriterion}
            description={formDescription}
            onCriterionChange={setFormCriterion}
            onDescriptionChange={setFormDescription}
            onSave={handleSaveCriteria}
            onCancel={() => setView({ type: 'list' })}
            isSaveDisabled={getFormSaveDisabled()}
          />
        );
      case 'delete': {
        const deleteIndex = findIndexByKey(view.key);

        if (deleteIndex === -1) return null;

        const item = localCriteria[deleteIndex];

        return (
          <CriteriaDeleteConfirm
            criterion={item.criterion}
            description={item.description}
            onConfirm={handleConfirmDelete}
            onCancel={() => setView({ type: 'list' })}
          />
        );
      }
    }
  };

  const colorLabel = CATEGORY_COLORS.find((c) => c.id === localCategory.color)?.name ?? localCategory.color;
  const dotColor = COLOR_DOT_MAP[localCategory.color] ?? colors.black25;

  const handleConfirmDeleteCategory = () => {
    setShowDeleteConfirm(false);
    onDeleteCategory();
  };

  return (
    <>
      <PanelContainer>
        <PanelHeader>
          <PanelTitleRow>
            <TitleWithMenu>
              <PanelTitle>Criteria Category - {localCategory.name}</PanelTitle>
              {isInherited ? (
                <InheritedBadge>Inherited</InheritedBadge>
              ) : (
                <Menu>
                  <Menu.Trigger>
                    <MenuTriggerButton data-testid='criteria-slideout-category-menu'>
                      <EllipsisIcon width={16} height={16} />
                    </MenuTriggerButton>
                  </Menu.Trigger>
                  <Menu.Content position='right' style={{ padding: 0, minWidth: 180 }}>
                    <Menu.Item onClick={() => setShowEditModal(true)}>
                      <Menu.Icon>
                        <PencilIcon width={16} height={16} />
                      </Menu.Icon>
                      Edit Category
                    </Menu.Item>
                    <Menu.Item onClick={() => setShowDeleteConfirm(true)}>
                      <Menu.Icon>
                        <DeactivateIcon width={16} height={16} color={colors.red100} />
                      </Menu.Icon>
                      <span style={{ color: colors.red100 }}>Delete Category</span>
                    </Menu.Item>
                  </Menu.Content>
                </Menu>
              )}
            </TitleWithMenu>
            <CloseButton onClick={handleClose}>&times;</CloseButton>
          </PanelTitleRow>
          <SubHeaderBar>
            <ColorDisplay>
              Display Color: <ColorDot $color={dotColor} /> {colorLabel}
            </ColorDisplay>
          </SubHeaderBar>
        </PanelHeader>

        <PanelContent>{getContent()}</PanelContent>

        <PanelFooter>
          <Button variant='ghost' onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!hasChanges}>
            Apply
          </Button>
        </PanelFooter>
      </PanelContainer>

      <NewCategoryModal
        show={showEditModal}
        onCancel={() => setShowEditModal(false)}
        onSubmit={(updates) => {
          // Buffer category edits locally -- they'll be flushed on "Apply"
          setLocalCategory(updates);

          // Update local criteria to reflect the new category name
          if (updates.name !== localCategory.name) {
            setLocalCriteria((prev) => prev.map((c) => ({ ...c, category: updates.name })));
          }

          setHasChanges(true);
          setShowEditModal(false);
        }}
        editingCategory={{ ...category, name: localCategory.name, color: localCategory.color }}
        existingNames={allCategories.filter((c) => c._tempId !== category._tempId).map((c) => c.name)}
      />

      {showDeleteConfirm && (
        <CategoryDeleteModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDeleteCategory}
          cascadesToGroups={cascadesToGroups}
        />
      )}
    </>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  padding: 20px 24px 0;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const PanelTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
`;

const TitleWithMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InheritedBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.black75};
  border: 0.5px solid ${colors.black25};
  background-color: ${colors.black5};
  padding: 3px 8px;
  border-radius: 16px;
`;

const MenuTriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.black50};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${colors.black50};
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
`;

const SubHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
`;

const ColorDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${colors.black75};
`;

const ColorDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const PanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
`;
